import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, X, ArrowLeft, Save } from 'lucide-react';
import AdminNav from '../components/AdminNav';
import { useLanguage } from '../i18n/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_MODES = [
  { key: 'standard', label: 'Standard' },
  { key: 'review_shield', label: 'B-Heard' },
  { key: 'review_shield_gift', label: 'B-Heard + Gift' },
  { key: 'global', label: 'Global' },
];

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const ProfileManagerPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', description: '', allowed_modes: ['standard'] });

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/api/demo-profiles');
      setProfiles(data.items || []);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const startEdit = (profile) => {
    setEditing(profile?.id || 'new');
    setForm(profile ? { name: profile.name, description: profile.description || '', allowed_modes: profile.allowed_modes || [] } : { name: '', description: '', allowed_modes: ['standard'] });
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (editing === 'new') {
        await apiFetch('/api/demo-profiles', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      } else {
        await apiFetch(`/api/demo-profiles/${editing}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      }
      setEditing(null); fetchProfiles();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Delete profile "${profile.name}"?`)) return;
    try { await apiFetch(`/api/demo-profiles/${profile.id}`, { method: 'DELETE' }); fetchProfiles(); } catch (err) { setError(err.message); }
  };

  const toggleMode = (key) => {
    setForm(prev => ({
      ...prev,
      allowed_modes: prev.allowed_modes.includes(key) ? prev.allowed_modes.filter(m => m !== key) : [...prev.allowed_modes, key]
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-manager-page">
      <AdminNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {error} <button onClick={() => setError('')} className="float-right"><X className="w-4 h-4" /></button>
          </div>
        )}

        {editing !== null ? (
          <div className="space-y-4" data-testid="profile-editor">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setEditing(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                <h1 className="text-xl font-bold text-slate-900">{editing === 'new' ? t('admin.profiles.new') : t('admin.profiles.edit')}</h1>
              </div>
              <button onClick={handleSave} disabled={saving || !form.name} data-testid="save-profile-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl">
                <Save className="w-4 h-4" /> {saving ? t('admin.common.saving') : t('admin.common.save')}
              </button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.profiles.name')}</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full max-w-sm px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" data-testid="profile-name-input" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.profiles.description')}</label>
                <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full max-w-lg px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" data-testid="profile-desc-input" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 block">{t('admin.profiles.allowedModes')}</label>
                <div className="space-y-2">
                  {ALL_MODES.map(m => (
                    <label key={m.key} className="flex items-center gap-3 cursor-pointer">
                      <button onClick={() => toggleMode(m.key)} data-testid={`profile-mode-${m.key}`}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${form.allowed_modes.includes(m.key) ? 'bg-[#0077FF] border-[#0077FF]' : 'border-slate-300'}`}>
                        {form.allowed_modes.includes(m.key) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </button>
                      <span className="text-sm text-slate-700">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="profile-list">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900">{t('admin.profiles.title')}</h1>
                <p className="text-sm text-slate-500 mt-0.5">{t('admin.profiles.subtitle')}</p>
              </div>
              <button onClick={() => startEdit(null)} data-testid="create-profile-btn"
                className="flex items-center gap-2 px-4 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] text-white text-sm font-semibold rounded-xl">
                <Plus className="w-4 h-4" /> {t('admin.profiles.new')}
              </button>
            </div>
            {loading ? <div className="text-center py-16 text-slate-400">{t('admin.common.loading')}</div> : profiles.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl"><p className="text-slate-400 text-sm">{t('admin.common.noItems')}</p></div>
            ) : (
              <div className="space-y-2">
                {profiles.map(p => (
                  <div key={p.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between" data-testid={`profile-row-${p.id}`}>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{p.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{p.description || 'No description'}</p>
                      <div className="flex gap-1.5 mt-2">
                        {(p.allowed_modes || []).map(m => (
                          <span key={m} className="px-2 py-0.5 rounded-full bg-blue-50 text-[10px] font-medium text-[#0077FF]">{m.replace(/_/g, ' ')}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(p)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" data-testid={`edit-profile-${p.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(p)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-profile-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileManagerPage;
